import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { MapPin, Mail, Phone, Clock, Shield, Zap, Send, Hexagon, ChevronRight } from 'lucide-react';

// Components
import PageHeader from '../components/ui/PageHeader';
import ParticleBackground from '../components/ui/ParticleBackground';

const Contact = () => {
  const [glitchText, setGlitchText] = useState(false);
  
  useEffect(() => {
    window.scrollTo(0, 0);
    
    // Random glitch effect
    const glitchInterval = setInterval(() => {
      setGlitchText(true);
      setTimeout(() => setGlitchText(false), 200);
    }, 8000);
    
    return () => clearInterval(glitchInterval);
  }, []);

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    organization: '',
    phone: '',
    message: '',
    interest: ''
  });

  const [submitStatus, setSubmitStatus] = useState(null);
  const [error, setError] = useState(null);
  const [focusedField, setFocusedField] = useState(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitStatus('sending');
    setError(null);
    
    try {
      // Formspree endpoint - replace YOUR_FORM_ID with your actual Formspree form ID
      const formspreeEndpoint = 'https://formspree.io/f/mldbgdag';
      
      const response = await fetch(formspreeEndpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(formData),
      });
      
      const responseData = await response.json();
      
      if (!response.ok) {
        throw new Error(responseData.error || 'Failed to submit form');
      }
      
      // Success
      setSubmitStatus('success');
      // Reset form after successful submission
      setFormData({
        name: '',
        email: '',
        organization: '',
        phone: '',
        message: '',
        interest: ''
      });
    } catch (err) {
      console.error('Error submitting form:', err);
      setError(err.message || 'Something went wrong. Please try again.');
      setSubmitStatus('error');
    }
  };

  // Advanced tech grid with circuit patterns
  const TechGrid = () => (
    <div className="absolute inset-0 z-0 overflow-hidden">
      {/* Main grid */}
      <div className="absolute w-full h-full grid grid-cols-16 gap-px opacity-10">
        {[...Array(16)].map((_, i) => (
          <motion.div 
            key={i} 
            className="border-r border-gray-400/30 h-full"
            animate={{
              opacity: [0.1, 0.3, 0.1],
              borderColor: ["rgba(156, 163, 175, 0.1)", "rgba(156, 163, 175, 0.3)", "rgba(156, 163, 175, 0.1)"]
            }}
            transition={{
              duration: 4,
              repeat: Infinity,
              delay: i * 0.2
            }}
          />
        ))}
      </div>
      <div className="absolute w-full h-full grid grid-rows-16 gap-px opacity-10">
        {[...Array(16)].map((_, i) => (
          <motion.div 
            key={i} 
            className="border-b border-gray-400/30 w-full"
            animate={{
              opacity: [0.1, 0.3, 0.1],
              borderColor: ["rgba(156, 163, 175, 0.1)", "rgba(156, 163, 175, 0.3)", "rgba(156, 163, 175, 0.1)"]
            }}
            transition={{
              duration: 4,
              repeat: Infinity,
              delay: i * 0.15
            }}
          />
        ))}
      </div>
      
      {/* Circuit patterns */}
      <div className="absolute top-20 left-20 w-32 h-32 opacity-20">
        <motion.svg viewBox="0 0 100 100" className="w-full h-full">
          <motion.path
            d="M10,10 L90,10 L90,50 L50,50 L50,90 L10,90 Z"
            stroke="rgba(156, 163, 175, 0.5)"
            strokeWidth="1"
            fill="none"
            animate={{
              pathLength: [0, 1, 0],
              opacity: [0.2, 0.8, 0.2]
            }}
            transition={{
              duration: 6,
              repeat: Infinity,
              ease: "linear"
            }}
          />
          <motion.circle
            cx="50"
            cy="50"
            r="3"
            fill="rgba(156, 163, 175, 0.8)"
            animate={{
              scale: [1, 1.5, 1],
              opacity: [0.5, 1, 0.5]
            }}
            transition={{
              duration: 2,
              repeat: Infinity
            }}
          />
        </motion.svg>
      </div>
      
      {/* More circuit patterns */}
      <div className="absolute bottom-20 right-20 w-40 h-40 opacity-15">
        <motion.svg viewBox="0 0 100 100" className="w-full h-full">
          <motion.path
            d="M20,20 L80,20 L80,40 L60,40 L60,60 L80,60 L80,80 L20,80 Z"
            stroke="rgba(209, 213, 219, 0.4)"
            strokeWidth="1"
            fill="none"
            animate={{
              pathLength: [0, 1, 0],
              opacity: [0.1, 0.6, 0.1]
            }}
            transition={{
              duration: 8,
              repeat: Infinity,
              ease: "linear",
              delay: 2
            }}
          />
        </motion.svg>
      </div>
    </div>
  );

  // Subtle scan line effect
  const ScanLine = () => (
    <motion.div
      className="absolute inset-0 bg-gradient-to-b from-transparent via-gray-500/5 to-transparent h-24 pointer-events-none"
      animate={{
        top: ["-10%", "110%"],
      }}
      transition={{
        duration: 8,
        repeat: Infinity,
        ease: "linear"
      }}
    />
  );

  // Subtle holographic text effect
  const HolographicText = ({ children, className = "" }) => (
    <motion.div
      className={`relative ${className}`}
      animate={{
        textShadow: [
          "0 0 3px rgba(156, 163, 175, 0.3)",
          "0 0 6px rgba(156, 163, 175, 0.5)",
          "0 0 3px rgba(156, 163, 175, 0.3)"
        ]
      }}
      transition={{
        duration: 4,
        repeat: Infinity,
        ease: "easeInOut"
      }}
    >
      {children}
    </motion.div>
  );

  // Glitch effect for text
  const GlitchText = ({ children, isGlitching }) => (
    <motion.div
      className="relative"
      animate={isGlitching ? {
        x: [0, -2, 2, 0],
        textShadow: [
          "none",
          "2px 0 #888888, -2px 0 #cccccc",
          "none"
        ]
      } : {}}
      transition={{
        duration: 0.2,
        ease: "easeInOut"
      }}
    >
      {children}
    </motion.div>
  );

  return (
    <>
      <PageHeader 
        title="Contact Us" 
        subtitle="Get in touch with our team"
        backgroundImage="/images/contact-header.jpg"
      />

      {/* Contact Section */}
      <section className="py-20 bg-black relative overflow-hidden">
        <ParticleBackground />
        <TechGrid />
        <ScanLine />
        
        {/* Subtle decorative elements */}
        <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-gray-500/20 to-transparent"></div>
        <div className="absolute bottom-0 right-0 w-full h-px bg-gradient-to-r from-transparent via-gray-400/20 to-transparent"></div>
        
        {/* Corner tech elements */}
        <motion.div 
          className="absolute top-10 left-10 w-12 h-12 border-l border-t border-gray-500/20"
          animate={{
            opacity: [0.3, 0.6, 0.3]
          }}
          transition={{ duration: 6, repeat: Infinity }}
        />
        <motion.div 
          className="absolute bottom-10 right-10 w-12 h-12 border-r border-b border-gray-400/20"
          animate={{
            opacity: [0.3, 0.6, 0.3]
          }}
          transition={{ duration: 6, repeat: Infinity, delay: 3 }}
        />
        
        <div className="container mx-auto px-4 relative z-10">
          <motion.div 
            className="text-center mb-16"
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 1 }}
          >
            <HolographicText className="text-4xl md:text-6xl font-bold text-gray-100 mb-6 relative">
              <GlitchText isGlitching={glitchText}>
                Direct <span className="text-gray-400">Communication</span>
                <motion.span 
                  className="absolute -bottom-2 left-0 w-full h-px bg-gradient-to-r from-transparent via-gray-500/40 to-transparent"
                  initial={{ scaleX: 0, opacity: 0 }}
                  whileInView={{ scaleX: 1, opacity: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 1.5, delay: 0.4 }}
                />
              </GlitchText>
            </HolographicText>
          </motion.div>
          
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Contact Information */}
            <div className="lg:col-span-1">
              <motion.div
                initial={{ opacity: 0, y: 50, rotateX: -10 }}
                whileInView={{ opacity: 1, y: 0, rotateX: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 1, ease: [0.25, 0.46, 0.45, 0.94] }}
                className="bg-gradient-to-br from-gray-900/90 to-black/90 border border-gray-500/20 p-10 rounded-2xl h-full relative group backdrop-blur-sm"
                whileHover={{ 
                  y: -5,
                  boxShadow: "0 20px 50px rgba(156, 163, 175, 0.3), 0 0 50px rgba(156, 163, 175, 0.2)",
                  scale: 1.01
                }}
              >
                <motion.div 
                  className="absolute inset-0 border border-gray-400/0 rounded-2xl group-hover:border-gray-400/30 transition-all duration-700"
                  animate={{ 
                    boxShadow: [
                      "inset 0 0 0px rgba(156, 163, 175, 0)", 
                      "inset 0 0 20px rgba(156, 163, 175, 0.1)", 
                      "inset 0 0 0px rgba(156, 163, 175, 0)"
                    ]
                  }}
                  transition={{ duration: 4, repeat: Infinity }}
                />
                
                <motion.div 
                  className="inline-block p-px bg-gradient-to-r from-gray-600/40 via-gray-400/40 to-gray-600/40 rounded-md mb-6"
                  animate={{
                    boxShadow: [
                      "0 0 10px rgba(156, 163, 175, 0.1)",
                      "0 0 20px rgba(156, 163, 175, 0.2)",
                      "0 0 10px rgba(156, 163, 175, 0.1)"
                    ]
                  }}
                  transition={{ duration: 4, repeat: Infinity }}
                >
                  <div className="px-6 py-2 bg-gray-900/90 rounded-md text-sm text-gray-300 uppercase tracking-wider font-semibold backdrop-blur-sm">
                    Connect With Us
                  </div>
                </motion.div>
                
                <h2 className="text-3xl font-bold text-gray-100 mb-8 relative">
                  Contact <span className="text-gray-400">Information</span>
                  <motion.span 
                    className="absolute -bottom-2 left-0 w-full h-px bg-gradient-to-r from-transparent via-gray-500/40 to-transparent"
                    initial={{ scaleX: 0, opacity: 0 }}
                    whileInView={{ scaleX: 1, opacity: 1 }}
                    viewport={{ once: true }}
                    transition={{ duration: 1.5, delay: 0.4 }}
                  />
                </h2>
                
                <div className="space-y-8 mt-10">
                  <ContactItem 
                    icon={<MapPin size={22} />}
                    title="Address"
                    content={
                      <>
                        351 St Francis St<br />
                        Mobile, AL 36602
                      </>
                    }
                    delay={0.2}
                  />
                  
                  <ContactItem 
                    icon={<Mail size={22} />}
                    title="Email"
                    content={
                      <a href="mailto:Contact@ReticuliTech.com" className="text-gray-400 hover:text-gray-300 transition-colors duration-300">
                        Contact@ReticuliTech.com
                      </a>
                    }
                    delay={0.4}
                  />
                  
                  <ContactItem
                    icon={<Phone size={22} />}
                    title="Phone"
                    content={
                      <span className="text-gray-400">
                        Coming soon
                      </span>
                    }
                    delay={0.6}
                  />
                  
                  <ContactItem 
                    icon={<Clock size={22} />}
                    title="Hours"
                    content={
                      <>
                        Monday - Friday<br />
                        8:00 AM - 5:00 PM EST
                      </>
                    }
                    delay={0.8}
                  />
                </div>
                
                <motion.div 
                  className="mt-10 pt-8 border-t border-gray-700/20"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: 1.0 }}
                >
                  <div className="flex items-center mb-4">
                    <motion.div
                      className="mr-4 p-2 bg-gray-500/20 rounded-lg border border-gray-500/30"
                      whileHover={{ scale: 1.1, rotate: 360 }}
                      transition={{ duration: 0.5 }}
                    >
                      <Shield size={20} className="text-gray-400" />
                    </motion.div>
                    <span className="text-gray-200 font-semibold">Security First</span>
                  </div>
                  <p className="text-gray-500 text-sm leading-relaxed">
                    Our communications are secure and confidential. For sensitive inquiries, please contact us on Signal.
                  </p>
                </motion.div>
                
                {/* Enhanced tech decorations */}
                <motion.div 
                  className="absolute -top-2 -right-2 w-8 h-8 border-t-2 border-r-2 border-gray-500/20 opacity-0 group-hover:opacity-100 transition-opacity duration-500"
                  animate={{
                    boxShadow: [
                      "0 0 0px rgba(156, 163, 175, 0)",
                      "0 0 15px rgba(156, 163, 175, 0.3)",
                      "0 0 0px rgba(156, 163, 175, 0)"
                    ]
                  }}
                  transition={{ duration: 3, repeat: Infinity }}
                />
                <motion.div 
                  className="absolute -bottom-2 -left-2 w-8 h-8 border-b-2 border-l-2 border-gray-500/20 opacity-0 group-hover:opacity-100 transition-opacity duration-500"
                  animate={{
                    boxShadow: [
                      "0 0 0px rgba(156, 163, 175, 0)",
                      "0 0 15px rgba(156, 163, 175, 0.3)",
                      "0 0 0px rgba(156, 163, 175, 0)"
                    ]
                  }}
                  transition={{ duration: 3, repeat: Infinity, delay: 1.5 }}
                />
              </motion.div>
            </div>

            {/* Contact Form */}
            <motion.div
              initial={{ opacity: 0, y: 50, rotateX: -10 }}
              whileInView={{ opacity: 1, y: 0, rotateX: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 1, delay: 0.3, ease: [0.25, 0.46, 0.45, 0.94] }}
              className="lg:col-span-2"
            >
              <div className="bg-gradient-to-br from-gray-900/90 to-black/90 border border-gray-500/20 p-10 rounded-2xl relative group backdrop-blur-sm">
                <motion.div 
                  className="absolute inset-0 border border-gray-400/0 rounded-2xl group-hover:border-gray-400/30 transition-all duration-700"
                  animate={{ 
                    boxShadow: [
                      "inset 0 0 0px rgba(156, 163, 175, 0)", 
                      "inset 0 0 20px rgba(156, 163, 175, 0.1)", 
                      "inset 0 0 0px rgba(156, 163, 175, 0)"
                    ]
                  }}
                  transition={{ duration: 4, repeat: Infinity }}
                />
                
                <motion.div 
                  className="inline-block p-px bg-gradient-to-r from-gray-600/40 via-gray-400/40 to-gray-600/40 rounded-md mb-6"
                  animate={{
                    boxShadow: [
                      "0 0 10px rgba(156, 163, 175, 0.1)",
                      "0 0 20px rgba(156, 163, 175, 0.2)",
                      "0 0 10px rgba(156, 163, 175, 0.1)"
                    ]
                  }}
                  transition={{ duration: 4, repeat: Infinity }}
                >
                  <div className="px-6 py-2 bg-gray-900/90 rounded-md text-sm text-gray-300 uppercase tracking-wider font-semibold backdrop-blur-sm">
                    Message System
                  </div>
                </motion.div>
                
                <h2 className="text-3xl font-bold text-gray-100 mb-8 relative">
                  Get in <span className="text-gray-400">Touch</span>
                  <motion.span 
                    className="absolute -bottom-2 left-0 w-full h-px bg-gradient-to-r from-transparent via-gray-500/40 to-transparent"
                    initial={{ scaleX: 0, opacity: 0 }}
                    whileInView={{ scaleX: 1, opacity: 1 }}
                    viewport={{ once: true }}
                    transition={{ duration: 1.5, delay: 0.4 }}
                  />
                </h2>
                
                {submitStatus === 'success' ? (
                  <motion.div 
                    className="bg-gradient-to-br from-green-900/20 to-green-800/10 border border-green-500/30 rounded-xl p-10 text-center backdrop-blur-sm"
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.6 }}
                  >
                    <motion.div 
                      className="mb-8 p-4 bg-green-500/20 inline-block rounded-full border border-green-500/40 backdrop-blur-sm"
                      animate={{
                        boxShadow: [
                          "0 0 30px rgba(34, 197, 94, 0.3)",
                          "0 0 60px rgba(34, 197, 94, 0.6)",
                          "0 0 30px rgba(34, 197, 94, 0.3)"
                        ],
                        rotate: [0, 360]
                      }}
                      transition={{ 
                        boxShadow: { duration: 3, repeat: Infinity },
                        rotate: { duration: 20, repeat: Infinity, ease: "linear" }
                      }}
                    >
                      <Send size={32} className="text-green-400" />
                    </motion.div>
                    
                    <h3 className="text-2xl font-bold text-white mb-4">Transmission Complete</h3>
                    <p className="text-gray-300 mb-2">
                      Your message has been received and encrypted.
                    </p>
                    <p className="text-gray-400">
                      Our team will analyze your inquiry and respond shortly.
                    </p>
                  </motion.div>
                ) : (
                  <form onSubmit={handleSubmit}>
                    {submitStatus === 'error' && (
                      <motion.div 
                        className="mb-6 p-4 bg-gradient-to-br from-red-900/20 to-red-800/10 border border-red-500/30 rounded-lg backdrop-blur-sm"
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.5 }}
                      >
                        <p className="text-red-400">{error || 'An error occurred. Please try again.'}</p>
                      </motion.div>
                    )}
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                      <FormField
                        label="Name"
                        type="text"
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        focusedField={focusedField}
                        setFocusedField={setFocusedField}
                        required
                      />
                      
                      <FormField
                        label="Email"
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        focusedField={focusedField}
                        setFocusedField={setFocusedField}
                        required
                      />
                      
                      <FormField
                        label="Organization"
                        type="text"
                        id="organization"
                        name="organization"
                        value={formData.organization}
                        onChange={handleChange}
                        focusedField={focusedField}
                        setFocusedField={setFocusedField}
                      />
                      
                      <FormField
                        label="Phone (Optional)"
                        type="tel"
                        id="phone"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        focusedField={focusedField}
                        setFocusedField={setFocusedField}
                      />
                    </div>
                    
                    <div className="mb-6">
                      <label htmlFor="interest" className="block text-gray-200 font-medium mb-3">Area of Interest</label>
                      <div className="relative">
                        <select
                          id="interest"
                          name="interest"
                          value={formData.interest}
                          onChange={handleChange}
                          onFocus={() => setFocusedField('interest')}
                          onBlur={() => setFocusedField(null)}
                          required
                          className="w-full bg-black/80 border border-gray-700/50 rounded-lg px-4 py-3 text-gray-200 focus:outline-none focus:border-gray-500 transition-all duration-300 appearance-none backdrop-blur-sm"
                        >
                          <option value="">Select an Option</option>
                          <option value="Unity-Scholars-Program">Unity Scholars Program</option>
                          <option value="research">Research Partnership Opportunities</option>
                          <option value="general">General Inquiry</option>
                        </select>
                        <div className="absolute right-4 top-1/2 transform -translate-y-1/2 pointer-events-none">
                          <Hexagon size={16} className="text-gray-500" />
                        </div>
                        <motion.div 
                          className="absolute inset-0 border border-gray-500/0 rounded-lg pointer-events-none"
                          animate={{ 
                            borderColor: focusedField === 'interest' 
                              ? ['rgba(156, 163, 175, 0.2)', 'rgba(156, 163, 175, 0.5)', 'rgba(156, 163, 175, 0.2)'] 
                              : 'rgba(156, 163, 175, 0)'
                          }}
                          transition={{ duration: 2, repeat: focusedField === 'interest' ? Infinity : 0 }}
                        />
                      </div>
                    </div>
                    
                    <div className="mb-8">
                      <label htmlFor="message" className="block text-gray-200 font-medium mb-3">Message</label>
                      <div className="relative">
                        <textarea
                          id="message"
                          name="message"
                          value={formData.message}
                          onChange={handleChange}
                          onFocus={() => setFocusedField('message')}
                          onBlur={() => setFocusedField(null)}
                          required
                          rows="5"
                          className="w-full bg-black/80 border border-gray-700/50 rounded-lg px-4 py-3 text-gray-200 focus:outline-none focus:border-gray-500 transition-all duration-300 backdrop-blur-sm"
                        ></textarea>
                        <motion.div 
                          className="absolute inset-0 border border-gray-500/0 rounded-lg pointer-events-none"
                          animate={{ 
                            borderColor: focusedField === 'message' 
                              ? ['rgba(156, 163, 175, 0.2)', 'rgba(156, 163, 175, 0.5)', 'rgba(156, 163, 175, 0.2)'] 
                              : 'rgba(156, 163, 175, 0)'
                          }}
                          transition={{ duration: 2, repeat: focusedField === 'message' ? Infinity : 0 }}
                        />
                      </div>
                    </div>
                    
                    <motion.button
                      type="submit"
                      disabled={submitStatus === 'sending'}
                      className={`relative px-8 py-4 bg-gradient-to-r from-gray-600 to-gray-800 hover:from-gray-500 hover:to-gray-700 text-white font-semibold rounded-xl transition-all duration-500 overflow-hidden group ${
                        submitStatus === 'sending' ? 'opacity-75 cursor-not-allowed' : ''
                      }`}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      <motion.div
                        className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent"
                        animate={{
                          x: ["-100%", "100%"]
                        }}
                        transition={{
                          duration: 2,
                          repeat: Infinity,
                          ease: "linear"
                        }}
                      />
                      <span className="relative z-10 flex items-center">
                        {submitStatus === 'sending' ? 'Transmitting...' : 'Send Message'}
                        <Send size={18} className="ml-2 group-hover:translate-x-1 transition-transform duration-300" />
                      </span>
                    </motion.button>
                  </form>
                )}
                
                {/* Enhanced tech decorations */}
                <motion.div 
                  className="absolute -top-2 -right-2 w-8 h-8 border-t-2 border-r-2 border-gray-500/20 opacity-0 group-hover:opacity-100 transition-opacity duration-500"
                  animate={{
                    boxShadow: [
                      "0 0 0px rgba(156, 163, 175, 0)",
                      "0 0 15px rgba(156, 163, 175, 0.3)",
                      "0 0 0px rgba(156, 163, 175, 0)"
                    ]
                  }}
                  transition={{ duration: 3, repeat: Infinity }}
                />
                <motion.div 
                  className="absolute -bottom-2 -left-2 w-8 h-8 border-b-2 border-l-2 border-gray-500/20 opacity-0 group-hover:opacity-100 transition-opacity duration-500"
                  animate={{
                    boxShadow: [
                      "0 0 0px rgba(156, 163, 175, 0)",
                      "0 0 15px rgba(156, 163, 175, 0.3)",
                      "0 0 0px rgba(156, 163, 175, 0)"
                    ]
                  }}
                  transition={{ duration: 3, repeat: Infinity, delay: 1.5 }}
                />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

    </>
  );
};

// Enhanced Contact Item Component
const ContactItem = ({ icon, title, content, delay }) => {
  return (
    <motion.div 
      className="flex items-start group/item"
      initial={{ opacity: 0, x: -20 }}
      whileInView={{ opacity: 1, x: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.6, delay }}
    >
      <motion.div 
        className="mr-4 p-3 bg-gray-500/20 rounded-lg text-gray-400 border border-gray-500/30 group-hover/item:border-gray-400/50 transition-all duration-300 backdrop-blur-sm"
        whileHover={{ scale: 1.1, rotate: 360 }}
        transition={{ duration: 0.5 }}
        animate={{
          boxShadow: [
            "0 0 0px rgba(156, 163, 175, 0)",
            "0 0 10px rgba(156, 163, 175, 0.2)",
            "0 0 0px rgba(156, 163, 175, 0)"
          ]
        }}
      >
        {icon}
      </motion.div>
      <div>
        <h3 className="text-gray-200 font-semibold mb-2 group-hover/item:text-white transition-colors duration-300">{title}</h3>
        <div className="text-gray-500 group-hover/item:text-gray-400 transition-colors duration-300">
          {content}
        </div>
      </div>
    </motion.div>
  );
};

// Enhanced Form Field Component
const FormField = ({ label, type, id, name, value, onChange, focusedField, setFocusedField, required = false }) => {
  return (
    <div>
      <label htmlFor={id} className="block text-gray-200 font-medium mb-3">{label}</label>
      <div className="relative">
        <input
          type={type}
          id={id}
          name={name}
          value={value}
          onChange={onChange}
          onFocus={() => setFocusedField(name)}
          onBlur={() => setFocusedField(null)}
          required={required}
          className="w-full bg-black/80 border border-gray-700/50 rounded-lg px-4 py-3 text-gray-200 focus:outline-none focus:border-gray-500 transition-all duration-300 backdrop-blur-sm"
        />
        <motion.div 
          className="absolute inset-0 border border-gray-500/0 rounded-lg pointer-events-none"
          animate={{ 
            borderColor: focusedField === name 
              ? ['rgba(156, 163, 175, 0.2)', 'rgba(156, 163, 175, 0.5)', 'rgba(156, 163, 175, 0.2)'] 
              : 'rgba(156, 163, 175, 0)'
          }}
          transition={{ duration: 2, repeat: focusedField === name ? Infinity : 0 }}
        />
      </div>
    </div>
  );
};

export default Contact;